import React, { FC, useRef } from 'react';
import { Headline4, Headline5, Headline6, TextDefault } from '../../../Text';
import { Flexbox, FlexboxCenter, FlexboxVertical, FlexContainer, ListULContainer } from '../../../Layout';
import { PanelWithGradient } from '../../../PanelWithGradient';
import styled from 'styled-components';
import { Colors } from '../../../../../styles/colors';
import { Icon } from '../../../Icon';
import { PieChart } from '../../../PieChart';
import { Chart, Product } from '../../../../..';
import { useAttachmentDataUrl } from '../../../../../utils/Attachments';
import { AnimatedContainerScroll } from '../../../AnimatedContainer';

const PaymentConditionsSectionWrapper = styled(FlexboxVertical)`
  ${Flexbox} {
    align-items: flex-start;
  }

  ${PanelWithGradient} {
    max-width: 24rem;
    margin-right: 2.5rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Panel = styled.div`
  background-color: ${Colors.grayMid};
  color: ${Colors.white};
  padding: 2rem;
  
  ${Flexbox} {
    align-items: center;
    margin-bottom: 1rem;
  }
  
  ${Headline5} {
    margin-left: .75rem;
  }
`;

const SuccessIconWrapper = styled(FlexboxCenter)`
  padding: .25rem;
  border-radius: 50%;
  background-color: ${Colors.secondary};
`;

const ImageWrapper = styled(Flexbox)`
  grid-column: span 2;
  margin: 0 3rem;
  
  & > * {
    flex: 1;
    
    &:first-child {
      z-index: 0;
      margin-right: -7rem;
      margin-bottom: -4.5rem;
      min-height: 12rem;
    }
    
    &:last-child {
      z-index: 1;
      margin: 7.5rem 0 1.5rem;
      min-height: 6rem;
    }
  }
`;

const SingleLineInGrid = styled(FlexboxVertical)`
  grid-column: span 2;
`;

const DoubleHeadlineWrapper = styled(FlexboxVertical)`
  & > * {
    margin-bottom: .25rem !important;
  }
`;

const CenterBox = styled(Flexbox)`
  margin: 0 auto;
`;

const BottomTableWrapper = styled(Flexbox)`
  margin-bottom: 3rem;

  & > * {
    &:first-child {
      min-width: 10rem;
      padding-right: 2rem;    
    }
    
    &:last-child {
      align-items: flex-end;
      margin-left: auto;
    }
  }
`;

const PieWrapper = styled(FlexboxCenter)`
  max-width: 18.5rem;
  max-height: 18.5rem;
  width: 100%;
  margin: 1.5rem auto;
`;

interface Props {
  product?: Product;
  visible?: boolean;
}
export const PaymentConditionsPanel: FC<Props> = ({ product, visible = true, ...props }) => {
  const panelViewRef = useRef<HTMLDivElement | null>(null);
  const pieCharts = product?.metaProduct?.productFeatures?.pieCharts?.charts || [];
  const hintImageUrl = useAttachmentDataUrl(product?.metaProduct?.productFeatures?.hintImageId);

  return (
    <ListULContainer>
      <PaymentConditionsSectionWrapper>
        <Headline4>Zahlungskonditionen</Headline4>

        <ContentGrid>
          <TextDefault dangerouslySetInnerHTML={{ __html: product?.metaProduct?.productFeatures?.termsOfPayment }} />

          <ImageWrapper>
            <Image alt='' src={hintImageUrl} />
            <AnimatedContainerScroll viewRef={panelViewRef}>
              {(animationDelta) => (
                <Panel ref={panelViewRef} style={{ transform: `translateY(calc(-7.5rem * ${1 - animationDelta}))` }}>
                  <Flexbox>
                    <SuccessIconWrapper>
                      <Icon icon={'info-circle'} size={24} />
                    </SuccessIconWrapper>
                    <Headline5>Unser Tipp</Headline5>
                  </Flexbox>
                  <TextDefault dangerouslySetInnerHTML={{ __html: product?.metaProduct?.productFeatures?.hint }} />
                </Panel>
              )}
            </AnimatedContainerScroll>
          </ImageWrapper>

          {
            product?.metaProduct?.productFeatures?.pieCharts &&
            <>
            <SingleLineInGrid>
              <CenterBox>
                <DoubleHeadlineWrapper>
                  <Headline4 dangerouslySetInnerHTML={{ __html: product?.metaProduct?.productFeatures?.pieCharts?.title }} />
                  <TextDefault dangerouslySetInnerHTML={{ __html: product?.metaProduct?.productFeatures?.pieCharts?.subTitle }} />
                </DoubleHeadlineWrapper>
              </CenterBox>
            </SingleLineInGrid>

            {
              (pieCharts || []).reduce((res: Chart[][], chart, index) => {
                const resIndex = Math.floor(index / 2);
                const resItem = res.length > resIndex && res[resIndex] || [];
                return [...res.filter((i,index) => index !== resIndex), [ ...resItem, chart ]];
              }, []).map((charts) => (
                <PieChartDuo visible={visible} pieCharts={charts} />
              ))
            }
            </>
          }

        </ContentGrid>
      </PaymentConditionsSectionWrapper>
    </ListULContainer>
  );
}

const PieChartDuo: FC<{ pieCharts: Chart[], visible?: boolean }> = ({ pieCharts, visible = true }) => (
  <>
  <CenterBox>
    <DoubleHeadlineWrapper>
      <Headline6>
        { pieCharts.length > 0 && pieCharts[0].title }
      </Headline6>
      <TextDefault>
        { pieCharts.length > 0 && pieCharts[0].subTitle }
      </TextDefault>
    </DoubleHeadlineWrapper>
  </CenterBox>
  <CenterBox>
    <DoubleHeadlineWrapper>
      <Headline6>
        { pieCharts.length > 1 && pieCharts[1].title }
      </Headline6>
      <TextDefault>
        { pieCharts.length > 1 && pieCharts[1].subTitle }
      </TextDefault>
    </DoubleHeadlineWrapper>
  </CenterBox>

  <PieWrapper>
    {
      pieCharts.length > 0 && pieCharts[0].data?.length > 0 &&
      <PieChart visible={visible} proportions={[
        ...pieCharts[0].data.map(({ key, value }, index) => ({
          percentage: +value,
          title: key,
          color: [
            '#9B9B9A',
            '#DEDEDE',
            '#6DB9E1',
            '#838382',
            '#B7BB10',
            '#687D24',
            Colors.secondary,
            Colors.primary,
            Colors.tertiary,
          ][index % 9],
        })),
        // { color: '#9B9B9A', percentage: 4.3 },
        // { color: '#DEDEDE', percentage: 15 },
        // { color: '#6DB9E1', percentage: 3.9 },
        // { color: '#838382', percentage: .4 },
        // { color: '#B7BB10', percentage: 51.8 },
        // { color: '#687D24', percentage: 24.6 },
      ]} />
    }
  </PieWrapper>
  <PieWrapper>
    {
      pieCharts.length > 1 && pieCharts[1].data?.length > 0 &&
      <PieChart visible={visible} proportions={[
        ...pieCharts[1].data.map(({ key, value }, index) => ({
          percentage: +value,
          title: key,
          color: [
            '#9B9B9A',
            '#DEDEDE',
            '#6DB9E1',
            '#838382',
            '#B7BB10',
            '#687D24',
            Colors.secondary,
            Colors.primary,
            Colors.tertiary,
          ][index % 9],
        })),
        // { color: '#687D24', percentage: 52.9 },
        // { color: '#B7BB10', percentage: 47.1 },
        // { color: Colors.secondary, percentage: 33 },
        // { color: Colors.primary, percentage: 22 },
        // { color: Colors.tertiary, percentage: 45 },
      ]} />
    }
  </PieWrapper>

  <CenterBox>
    <BottomTableWrapper>
      <DoubleHeadlineWrapper>
        {
          pieCharts.length > 0 && pieCharts[0].legend?.map(({ key }, index) => (
            <TextDefault key={index}>{ key }</TextDefault>
          ))
        }
      </DoubleHeadlineWrapper>
      <DoubleHeadlineWrapper>
        {
          pieCharts.length > 0 && pieCharts[0].legend?.map(({ value }, index) => (
            <TextDefault key={index}>{ value }</TextDefault>
          ))
        }
      </DoubleHeadlineWrapper>
    </BottomTableWrapper>
  </CenterBox>
  <CenterBox>
    <BottomTableWrapper>
      <DoubleHeadlineWrapper>
        {
          pieCharts.length > 1 && pieCharts[1].legend?.map(({ key }, index) => (
            <TextDefault key={index}>{ key }</TextDefault>
          ))
        }
      </DoubleHeadlineWrapper>
      <DoubleHeadlineWrapper>
        {
          pieCharts.length > 1 && pieCharts[1].legend?.map(({ value }, index) => (
            <TextDefault key={index}>{ value }</TextDefault>
          ))
        }
      </DoubleHeadlineWrapper>
    </BottomTableWrapper>
  </CenterBox>
  </>
)
